import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import oktaService from '@/services/OktaService';

// Session timeout set to 30 minutes (in milliseconds)
//make this one 2 minutes
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
// Warning before timeout (1 minute before)
const WARNING_BEFORE_TIMEOUT = 1 * 60 * 1000;

export default function SessionTimeout() {
    const navigate = useNavigate();
    const [lastActivity, setLastActivity] = useState<number>(Date.now());
    const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
    const [showTimeoutModal, setShowTimeoutModal] = useState<boolean>(false);
    const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
    const [remainingTime, setRemainingTime] = useState<number>(0);

    // Reset timer on user activity
    const resetTimer = () => {
        setLastActivity(Date.now());

        // Close warning modal if it's open
        if (showWarningModal) {
            setShowWarningModal(false);
        }
    };

    // Handle session extension
    const extendSession = async () => {
        try {
            // Try to refresh the token
            await oktaService.refreshToken();
            // Reset the timer
            resetTimer();
            // Close the warning modal
            setShowWarningModal(false);
        } catch (error) {
            console.error('Failed to extend session:', error);
            // If token refresh fails, logout
            handleTimeout();
        }
    };

    // Handle session timeout
    const handleTimeout = async () => {
        setShowWarningModal(false);
        setShowTimeoutModal(true);

        try {
            setIsLoggingOut(true);
            // Call logout with redirectToLogin set to false since we're handling the navigation ourselves
            await oktaService.logout(false);
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            // Navigate to login page
            setTimeout(() => {
                navigate('/login');
                setIsLoggingOut(false);
            }, 1500); // short delay to allow the user to see the message
        }
    };

    // Setup activity listeners
    useEffect(() => {
        // Activity events to track
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
        ];

        // Add event listeners
        activityEvents.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        // Cleanup event listeners
        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, []);

    // Check for session timeout
    useEffect(() => {
        // Set interval to check inactivity every minute
        const interval = setInterval(() => {
            const currentTime = Date.now();
            const timeSinceLastActivity = currentTime - lastActivity;

            // Calculate time left before timeout
            const timeLeft = INACTIVITY_TIMEOUT - timeSinceLastActivity;

            // Show warning modal if approaching timeout
            if (timeLeft <= WARNING_BEFORE_TIMEOUT && timeLeft > 0 && !showWarningModal && !showTimeoutModal) {
                setShowWarningModal(true);
                setRemainingTime(Math.floor(timeLeft / 1000));
            }

            // Handle timeout if inactive for too long
            if (timeSinceLastActivity >= INACTIVITY_TIMEOUT && !showTimeoutModal) {
                handleTimeout();
            }

            // Update remaining time if warning modal is open
            if (showWarningModal) {
                setRemainingTime(Math.max(0, Math.floor(timeLeft / 1000)));
            }
        }, 10000); // Check every 10 seconds to avoid heavy performance impact

        // Also update timer more frequently when warning modal is shown
        let warningInterval: number | null = null;
        if (showWarningModal) {
            warningInterval = window.setInterval(() => {
                const currentTime = Date.now();
                const timeSinceLastActivity = currentTime - lastActivity;
                const timeLeft = INACTIVITY_TIMEOUT - timeSinceLastActivity;
                setRemainingTime(Math.max(0, Math.floor(timeLeft / 1000)));

                if (timeLeft <= 0) {
                    handleTimeout();
                }
            }, 1000); // Update every second when warning is visible
        }

        // Cleanup intervals
        return () => {
            clearInterval(interval);
            if (warningInterval) clearInterval(warningInterval);
        };
    }, [lastActivity, showWarningModal, showTimeoutModal]);

    // Format remaining time as MM:SS
    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    // Don't render anything if not showing modals
    if (!showWarningModal && !showTimeoutModal) {
        return null;
    }

    return (
        <>
            {/* Warning Modal */}
            <Dialog open={showWarningModal} onOpenChange={setShowWarningModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Session Timeout Warning</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p>
                            Your session will expire in {formatTime(remainingTime)} due to inactivity.
                        </p>
                        <p className="mt-2">
                            Would you like to continue your session?
                        </p>
                    </div>
                    <DialogFooter>
                        <Button className=' bg-states-error' variant="outline" onClick={handleTimeout}>
                            Logout
                        </Button>
                        <Button className='bg-blue hover:bg-[#00336C]' onClick={extendSession}>
                            Continue Session
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Timeout Modal */}
            <Dialog open={showTimeoutModal} onOpenChange={setShowTimeoutModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Session Expired</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p>
                            Your session has expired due to inactivity.
                        </p>
                        <p className="mt-2">
                            Please log in again to continue.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => {
                            //close modal
                            setShowTimeoutModal(false);
                            //redirect to login
                            window.location.reload();
                        }} className='bg-blue hover:bg-[#00336C]' disabled={isLoggingOut}>
                            {isLoggingOut ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Logging out...
                                </>
                            ) : (
                                "OK"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
} 